import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Download, Calendar, FileText } from 'lucide-react';
import { axiosInstance } from '@/App';

export default function ClientPortalInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [groupedInvoices, setGroupedInvoices] = useState({});
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const response = await axiosInstance.get('/client-portal/invoices');
      setInvoices(response.data);
      groupInvoicesByMonth(response.data);
    } catch (error) {
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  // Get invoice date (handles multiple field names)
  const getInvoiceDate = (invoice) => {
    const dateStr = invoice.issue_date || invoice.date || invoice.created_at;
    if (!dateStr) return null;
    return new Date(dateStr);
  };

  // Get invoice total (handles multiple field names)
  const getInvoiceTotal = (invoice) => {
    return invoice.total || invoice.total_with_tax || 0;
  };

  // Check if invoice is paid
  const isInvoicePaid = (invoice) => {
    return invoice.paid || invoice.status === 'paid' || invoice.paid_date != null;
  };

  const groupInvoicesByMonth = (invoices) => {
    const grouped = {};
    invoices.forEach(invoice => {
      const date = getInvoiceDate(invoice);
      if (!date || isNaN(date.getTime())) {
        // Handle invalid dates by putting them in "Sin fecha"
        const monthKey = '0000-00';
        if (!grouped[monthKey]) {
          grouped[monthKey] = {
            name: 'Sin fecha',
            invoices: []
          };
        }
        grouped[monthKey].invoices.push(invoice);
        return;
      }
      
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long' });
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          name: monthName.charAt(0).toUpperCase() + monthName.slice(1),
          invoices: []
        };
      }
      grouped[monthKey].invoices.push(invoice);
    });
    setGroupedInvoices(grouped);
  };

  const handleDownload = async (invoiceId, invoiceNumber) => {
    setDownloading(invoiceId);
    try {
      const response = await axiosInstance.get(`/client-portal/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `factura-${invoiceNumber || invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Factura descargada');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Error al descargar la factura. Intenta de nuevo más tarde.');
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (invoice) => {
    const date = getInvoiceDate(invoice);
    if (!date || isNaN(date.getTime())) return 'Fecha no disponible';
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#9C0F17] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/portal-cliente/dashboard')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <h1 className="text-2xl font-bold">Mis Facturas</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Cargando facturas...</p>
          </div>
        ) : Object.keys(groupedInvoices).length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600">No tienes facturas disponibles</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.keys(groupedInvoices)
              .sort((a, b) => b.localeCompare(a))
              .map(monthKey => (
                <Card key={monthKey}>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Calendar className="w-5 h-5 mr-2 text-[#9C0F17]" />
                      {groupedInvoices[monthKey].name}
                      <Badge variant="outline" className="ml-3">
                        {groupedInvoices[monthKey].invoices.length} factura{groupedInvoices[monthKey].invoices.length !== 1 ? 's' : ''}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {groupedInvoices[monthKey].invoices.map(invoice => (
                        <div
                          key={invoice.id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                        >
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              Factura #{invoice.invoice_number || 'Sin número'}
                            </p>
                            <p className="text-sm text-gray-600">
                              {formatDate(invoice)}
                            </p>
                          </div>
                          <div className="text-right mr-4">
                            <p className="font-bold text-lg text-gray-900">
                              {getInvoiceTotal(invoice).toFixed(2)} €
                            </p>
                            <Badge
                              className={isInvoicePaid(invoice) 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-yellow-100 text-yellow-700'}
                            >
                              {isInvoicePaid(invoice) ? 'Pagada' : 'Pendiente'}
                            </Badge>
                          </div>
                          <Button
                            onClick={() => handleDownload(invoice.id, invoice.invoice_number)}
                            size="sm"
                            className="bg-[#9C0F17] hover:bg-[#7A0C12]"
                            disabled={downloading === invoice.id}
                          >
                            {downloading === invoice.id ? (
                              <>
                                <span className="animate-spin mr-2">⏳</span>
                                Descargando...
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-2" />
                                Descargar
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        )}
      </main>
    </div>
  );
}
